import React, { type SyntheticEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type SxProps, type Theme } from "@mui/material/styles";

import Card, { CardBody, CardHeader, type CardProps } from "components/partials/card/card";
import { Tab, Tabs } from "ui/tabs";
import { Button } from "ui/button";

import { useParentEmailRules } from "hooks/use-parent-email-rules";

import { ClientAttributes } from "hooks/api/use-clients";
import { useSystemConfiguration } from "hooks/api/use-client-configurations";

import { ClientConfigurationDTOConfigKey } from "generated/schemas";

import Icons from "assets";

const CLIENT_RULES = "Client Rules";
const SYSTEM_RULES = "System Rules";
const PARENT_EMAIL_RULES_OPTIONS = [CLIENT_RULES, SYSTEM_RULES];

const EDIT_BUTTON_SX: SxProps<Theme> = { mt: 2 };

const RULES_TABS_SX: SxProps<Theme> = { mb: 3 };

interface ClientParentEmailCardProps extends CardProps {
  client: ClientAttributes;
}

export const ClientParentEmailCard: React.FC<ClientParentEmailCardProps> = ({
  client,
  ...rest
}) => {
  const parentEmailRules = useParentEmailRules(client.id);
  const { data: systemConfiguration } = useSystemConfiguration(
    ClientConfigurationDTOConfigKey.PARENT_EMAIL_PROMPT,
  );
  const systemEmailRules = systemConfiguration?.configValue ?? "";
  const [toggleValue, setToggleValue] = useState<string>(CLIENT_RULES);

  const navigate = useNavigate();

  const handleEditClick = () => {
    navigate("edit/parent-email-rules");
  };

  const handleTabChange = (_event: SyntheticEvent, option: string) => {
    setToggleValue(option);
  };

  const displayedRules =
    toggleValue === CLIENT_RULES
      ? parentEmailRules || "There are no Client rules."
      : systemEmailRules;

  return (
    <Card {...rest}>
      <div className="flex justify-between items-start mr-4">
        <CardHeader
          heading="Parent Email Rules"
          subscript="The rules will be applied during Parent Email generation."
        />
        {toggleValue === CLIENT_RULES && (
          <Button
            sx={EDIT_BUTTON_SX}
            variant="outlined"
            size="small"
            startIcon={<Icons.Edit />}
            onClick={handleEditClick}>
            Edit
          </Button>
        )}
      </div>
      <CardBody sx={{ pt: 0 }}>
        <Tabs
          aria-label="parent-email-rules-toggle"
          value={toggleValue}
          onChange={handleTabChange}
          sx={RULES_TABS_SX}>
          {PARENT_EMAIL_RULES_OPTIONS.map((name) => (
            <Tab key={name} value={name} label={name} />
          ))}
        </Tabs>
        {displayedRules && (
          <div className="whitespace-pre-wrap max-h-72 overflow-y-auto">{displayedRules}</div>
        )}
      </CardBody>
    </Card>
  );
};
